// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { IconWrapper } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import cartLineSharedStyles from "@rpsc/App/SharedStyles/CartLineSharedStyles";
import { productListLineStyles } from "@rpsc/Overrides/Widgets/MyListsDetails/MyListsDetailsProductListLine";
import { fontConfig } from "@rpsc/XDL/PreStyle/typography";
import { displayFlexOnPrint, sharedProductDescriptionStyles, xScreen } from "@rpsc/XDL/Utilities";
import { mergeWithCss } from "@rpsc/XDL/Utilities";
import { css } from "styled-components";

// ============== Checbox Section ===================//
productListLineStyles.selectedCheckbox = {
    css: css`
        @media print {
            display: none;
        }
        margin: 0;
    `,
};
// ================================================== //

// ================ Image Section ===================== //
productListLineStyles.productImageStyles = {
    linkWrappingImage: {
        css: css`
            width: 120px;
            height: 120px;
            justify-content: center;
            ${xScreen("lte", "md")} {
                width: 99px;
                height: 99px;
            }
        `,
    },
    img: {
        css: css`
            width: auto;
            height: auto;
            max-height: 100%;
            max-width: 100%;
            object-fit: contain;
        `,
    },
};
// ================================================== //

// ================ Info Section ==================== //
productListLineStyles.productDescriptionStyles = {
    ...sharedProductDescriptionStyles(),
};

productListLineStyles.productBrandStyles = {
    ...cartLineSharedStyles.productBrand,
};

productListLineStyles.restrictedDescriptionText = {
    ...sharedProductDescriptionStyles().productDetailDescription,
};

productListLineStyles.partNumbersSection = {
    css: css`
        display: flex;
        justify-content: space-between;
        margin-bottom: 12px;
        > ${StyledWrapper},>${TypographyStyle} {
            width: 50%;
        }
        ${xScreen("lte", "md")} {
            flex-direction: column;
            row-gap: 8px;
            > ${StyledWrapper},>${TypographyStyle} {
                width: 100%;
            }
        }
        @media print {
            flex-direction: column;
        }
    `,
};

productListLineStyles.productPartNumbersStyles = {
    ...mergeWithCss(cartLineSharedStyles.productPartNumbers, {
        container: {
            css: css`
                margin-bottom: 0;
            `,
        },
    }),
};

productListLineStyles.sortOrderGridItem = {
    width: 12,
    css: css`
        @media print {
            display: none;
        }
    `,
};
productListLineStyles.sortOrderTextField = {
    labelPosition: "left",
    sizeVariant: "small",
    cssOverrides: {
        formInputWrapper: css`
            width: 60px;
        `,
    },
    labelProps: {
        css: css`
            width: auto;
        `,
    },
};

productListLineStyles.createdOnText = {
    size: 12,
    lineHeight: "14px",
    italic: true,
    weight: "normal",
    color: "common.ui4",
    css: css`
        display: block;
    `,
};

productListLineStyles.restrictedMessageText = {
    css: css`
        margin-top: 12px;
    `,
};

// ================ Actions and price Section ================== //
productListLineStyles.quantityBreakPricing = {
    viewLink: {
        typographyProps: {
            size: 12,
        },
        css: css`
            margin-bottom: 8px;
        `,
    },
};

productListLineStyles.productPriceStyles = {
    priceWrapper: {
        css: css`
            line-height: 19px;
            margin-bottom: 4px;
        `,
    },
    price: {
        priceText: {
            size: 18,
            weight: "bold",
            lineHeight: "24px",
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                size: 12,
                lineHeight: "14px",
                weight: "normal",
                css: css`
                    white-space: nowrap;
                `,
            },
        },
    },
};

productListLineStyles.addToCartButton = {
    mergeCss: true,
    sizeVariant: "small",
    css: css`
        flex: 1;
        max-width: 100%;
        width: 100%;
    `,
};

productListLineStyles.quantityAddtoCartWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        width: 100%;
        gap: 8px;
        @media print {
            flex-direction: column;
            align-items: flex-start;
            gap: 5px;
        }
    `,
};
productListLineStyles.updateQuantityLink = {
    mergeCss: true,
    typographyProps: {
        size: 12,
        weight: "bold",
        css: css`
            line-height: ${fontConfig.lineHeight.xxSmall};
        `,
    },
};
productListLineStyles.quantityUpdatedWrapper = {
    css: css`
        line-height: ${fontConfig.lineHeight.xxSmall};
        @media print {
            display: none;
        }
    `,
};
productListLineStyles.quantityUpdatedIcon = {
    src: Check,
    color: "success",
    size: 11,
    css: css`
        margin-right: 3px;
    `,
};
productListLineStyles.quantityUpdatedText = {
    size: 12,
    lineHeight: "14px",
    weight: "bold",
    color: "success",
};

productListLineStyles.productAvailabilityStyles = {
    container: {
        css: css`
            margin-bottom: 8px;
        `,
        offsetCss: css`
            & > div:empty {
                display: none;
            }
        `,
    },
    errorWrapper: {
        css: css`
            margin-bottom: 12px;
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            ${displayFlexOnPrint}
        `,
    },
};

productListLineStyles.productUnitOfMeasureSelectStyles = {
    labelProps: {
        css: css`
            display: none;
            @media print {
                display: block;
            }
        `,
    },
    sizeVariant: "small",
    cssOverrides: {
        inputSelect: css`
            margin: 0;
        `,
        formField: css`
            width: 111px;
            ${IconWrapper} {
                background: none;
            }
        `,
    },
};

productListLineStyles.productQuantityOrderedStyles = {
    wrapper: {
        css: css`
            width: 70px;
            min-width: 70px;
        `,
    },
};

productListLineStyles.deleteLink = {
    mergeCss: true,
    icon: {
        iconProps: {
            src: X,
            size: 12,
            css: css`
                /* margin: 0 auto; */
                margin-right: 6px;
            `,
        },
    },
    typographyProps: {
        size: 12,
        lineHeight: "14px,",
    },
    css: css`
        display: flex;
        justify-content: center;
        margin-left: auto;
        margin-top: 4px;
        ${xScreen("lte", "md")} {
            margin-top: 12px;
        }
        &:focus {
            outline: none;
        }
    `,
};

productListLineStyles.deleteItemWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        align-self: flex-end;
        justify-content: space-between;
        align-items: flex-start;
        width: 100%;
        line-height: 14px;
        ${xScreen("lte", "md")} {
            flex-direction: row;
        }
    `,
};

productListLineStyles.updateQTY = {
    css: css`
        height: 17px;
        margin-top: 8px;
        ${xScreen("lte", "md")} {
            height: auto;
            margin-top: 12px;
        }
        ${xScreen("lte", "sm")} {
            margin-left: 27px;
        }
    `,
};

productListLineStyles.editNotesLink = {
    mergeCss: true,
    icon: {
        iconProps: {
            size: 12,
            css: css`
                margin-right: 6px;
            `,
        },
    },
    typographyProps: {
        size: 12,
    },
    css: css`
        display: flex;
        justify-content: center;
        ${xScreen("lte", "md")} {
            margin-top: 12px;
        }
    `,
};

productListLineStyles.purchasedDateGridItem = {
    width: 12,
    css: css`
        flex-direction: column;
        padding: 0 0 10px 0;
    `,
};

productListLineStyles.purchasedDateLabelText = {
    ...mergeWithCss(productListLineStyles.purchasedDateLabelText, {
        css: css`
            margin-bottom: 0;
        `,
    }),
};

// ==================== Notes Section ============================ //
productListLineStyles.editableNotesTextArea = {
    css: css`
        &&&& {
            overflow: auto;
            @media print {
                border: 0;
                overflow: hidden;
                max-width: 100%;
                box-shadow: none;
            }
        }
        max-width: 50%;
        ${xScreen("lte", "md")} {
            min-width: 100%;
            max-width: 100%;
        }
    `,
    labelProps: {
        css: css`
            @media print {
                text-align: left;
                font-weight: ${fontConfig.weight.medium};
            }
        `,
    },
};

productListLineStyles.notesStyles = {
    size: 14,
    weight: "normal",
    color: "common.ui4",
    css: css`
        letter-spacing: 0;
        line-height: 19px;
    `,
};
// ===================================================== //
